import { apiClient } from '../../core/api';
import { encryptPassword } from '../../core/encryption';
import { env } from '../../core/env';
import {
  PaymentStatus,
  type LoginCredentials,
  type LoginResponse,
  type LoginErrorResponse,
  type UserProfile,
  type ForgetPwdCodeModel,
  type SignupErrorResponse,
  type SignupPayload,
  type SignupResponse,
  type ResendEmailResponse,
  type ConfirmEmailPayload,
  type ConfirmEmailResponse,
  type GoogleOAuthInitiateRequest,
  type GoogleOAuthInitiateResponse,
  type GoogleOAuthCallbackResponse,
  type ShopifyHandoffResult,
} from '../../../types/auth';
import { normalizeError } from '../../utils/errorHandling';
import type { AppError } from '../../../types/errors';

const getEncryptionKey = (): string => {
  return env.authEncryptionKey;
};

export const loginWithCredentials = async (
  email: string,
  password: string
): Promise<LoginResponse | { requiresVerification: true; email: string }> => {
  try {
    // Encrypt the password using AES ECB PKCS7
    const finalEncryptionKey = getEncryptionKey();
    const encryptedPassword = encryptPassword(password, finalEncryptionKey);

    // Prepare login payload with location information
    const loginPayload: LoginCredentials = {
      email,
      password: encryptedPassword,
      isRememberMe: true,
    };

    const response = await apiClient.chatbotPost<LoginResponse | LoginErrorResponse>(
      '/auth/login',
      loginPayload
    );

    // Check if the response contains an error code
    const responseData = response.data as LoginResponse | LoginErrorResponse;

    if ('errorCode' in responseData) {
      const errorResponse = responseData as LoginErrorResponse;

      // Special handling for unconfirmed email
      if (
        errorResponse.errorCode == 400 &&
        errorResponse.errorMessage === 'User email unconfirmed'
      ) {
        return { requiresVerification: true as const, email };
      }

      // Convert legacy error to new error structure
      const appError = normalizeError(errorResponse);
      throw appError;
    }

    return responseData as LoginResponse;
  } catch (error) {
    // Re-throw AppError if it's already normalized
    if (error && typeof error === 'object' && 'subtype' in error) {
      throw error;
    }

    // Normalize other errors
    const appError = normalizeError(error);
    console.error('Login failed:', appError);
    throw appError;
  }
};

export const loginWithGoogle = async (code: string): Promise<LoginResponse> => {
  try {
    const response = await apiClient.chatbotPost<LoginResponse | LoginErrorResponse>(
      `/auth/login-google/${code}`,
      {}
    );

    // Check if the response contains an error code
    const responseData = response.data as LoginResponse | LoginErrorResponse;

    if ('errorCode' in responseData) {
      const errorResponse = responseData as LoginErrorResponse;
      const appError = normalizeError(errorResponse);
      throw appError;
    }

    return responseData as LoginResponse;
  } catch (error) {
    // Re-throw AppError if it's already normalized
    if (error && typeof error === 'object' && 'subtype' in error) {
      throw error;
    }

    // Normalize other errors
    const appError = normalizeError(error);
    console.error('Google login failed:', appError);
    throw appError;
  }
};

export const forgotPassword = async (email: string): Promise<UserProfile> => {
  try {
    const response = await apiClient.chatbotPost<UserProfile>(`/auth/forgot-password/${email}`, {});

    return response.data;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Forgot password request failed:', appError);
    throw appError;
  }
};

export const confirmForgotPassword = async (
  email: string,
  code: string,
  newPassword: string
): Promise<{ success?: boolean; errorCode?: string; errorMessage?: string }> => {
  try {
    // Encrypt the new password using the same encryption as login
    const finalEncryptionKey = getEncryptionKey();
    const encryptedPassword = encryptPassword(newPassword, finalEncryptionKey);

    const payload: ForgetPwdCodeModel = {
      email,
      code,
      password: encryptedPassword,
    };

    const response = await apiClient.chatbotPost<{
      success?: boolean;
      errorCode?: string;
      errorMessage?: string;
    }>('/auth/confirm-forgot-password', payload);

    return response.data;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Confirm forgot password request failed:', appError);
    throw appError;
  }
};

export const signupUser = async (
  firstName: string,
  lastName: string,
  org: string,
  email: string,
  password: string
): Promise<SignupResponse> => {
  try {
    // Encrypt the password
    const finalEncryptionKey = getEncryptionKey();
    const encryptedPassword = encryptPassword(password, finalEncryptionKey);

    // Prepare signup payload
    const signupPayload: SignupPayload = {
      email,
      organisation: org,
      password: encryptedPassword,
      firstName,
      lastName,
      status: 'disabled',
      paymentStatus: PaymentStatus.UNPAID,
    };

    const response = await apiClient.chatbotPost<SignupResponse | SignupErrorResponse>(
      '/auth/signup',
      signupPayload
    );

    // Check if the response contains an error code
    const responseData = response.data as SignupResponse | SignupErrorResponse;

    if ('errorCode' in responseData) {
      const errorResponse = responseData as SignupErrorResponse;
      const appError = normalizeError(errorResponse);
      throw appError;
    }

    // Note: We do NOT call bindReferralCode here because:
    // 1. The user has no auth token immediately after signup (still needs email verification)
    // 2. bind-referral endpoint is auth-protected — calling it now would 401 and trigger
    //    the global interceptor to redirect to login.
    // The fpr remains in localStorage and will be auto-bound by authStore.tryAutoBindReferral
    // after the user logs in (after email verification).
    return responseData as SignupResponse;
  } catch (error) {
    // Re-throw AppError if it's already normalized
    if (error && typeof error === 'object' && 'subtype' in error) {
      throw error;
    }

    // Normalize other errors
    const appError = normalizeError(error);
    console.error('Signup failed:', appError);
    throw appError;
  }
};

export const resendEmail = async (email: string): Promise<ResendEmailResponse> => {
  try {
    const response = await apiClient.chatbotPost<ResendEmailResponse>(
      `/auth/resend-email/${email}`,
      {}
    );

    return response.data;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Resend email request failed:', appError);
    throw appError;
  }
};

export const confirmEmail = async (email: string, code: string): Promise<ConfirmEmailResponse> => {
  try {
    const payload: ConfirmEmailPayload = {
      email,
      code,
    };

    const response = await apiClient.chatbotPost<ConfirmEmailResponse>(
      '/auth/confirm-email',
      payload
    );

    return response.data;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Confirm email request failed:', appError);
    throw appError;
  }
};

/**
 * Onboarding 2.0 email-first: set the real password on an already-activated
 * account (created earlier with a throwaway password), WITHOUT a verification
 * code. Backend uses Cognito adminSetUserPassword (permanent). The password is
 * AES-encrypted in transit, same as signup / reset.
 */
export const setPassword = async (email: string, password: string): Promise<UserProfile> => {
  try {
    const encryptedPassword = encryptPassword(password, getEncryptionKey());
    const response = await apiClient.chatbotPost<UserProfile>('/auth/set-password', {
      email,
      password: encryptedPassword,
    });
    return response.data;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Set password request failed:', appError);
    throw appError;
  }
};

export const getUser = async (userId: string): Promise<UserProfile> => {
  try {
    const response = await apiClient.chatbotGet<UserProfile>(`/users/${userId}`);
    const data = response.data as unknown;
    // The API client turns most failures into a *resolved* 200 whose body is an error
    // envelope ({errorCode, errorMessage, subtype}) — see api.ts' response interceptor.
    // Callers here persist the result as the signed-in user (authStore.refreshUserFromServer,
    // both login paths), and a persisted envelope has no id / stepName, so the app read the
    // merchant as brand-new and sent them to /business onboarding on every load until they
    // signed out (dev 2026-09-08, during a gateway outage: `user = {errorCode:500,
    // errorMessage:"Network Error"}`). A profile without an id is a failure, not a profile.
    if (!data || typeof data !== 'object' || !('id' in data) || !(data as UserProfile).id) {
      throw normalizeError(data ?? new Error('Empty user profile response'));
    }
    return data as UserProfile;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Get user request failed:', appError);
    throw appError;
  }
};

/**
 * Partial-update the user profile (backend PUT /users/{id} patches only the
 * fields provided — null fields are left untouched). Used by the Onboarding 2.0
 * Name step to persist firstName.
 */
export const updateUser = async (
  userId: string,
  fields: Partial<Pick<UserProfile, 'firstName' | 'lastName'>>
): Promise<UserProfile> => {
  try {
    const response = await apiClient.chatbotPut<UserProfile>(`/users/${userId}`, fields);
    return response.data;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Update user request failed:', appError);
    throw appError;
  }
};

/**
 * Initiate Google OAuth login flow for external (Shopify) login
 * This is function 1: POST to /oauth2/google/login
 */
export const initiateGoogleOAuthLogin = async (
  payload: GoogleOAuthInitiateRequest
): Promise<GoogleOAuthInitiateResponse> => {
  try {
    const response = await apiClient.chatbotPost<GoogleOAuthInitiateResponse>(
      `/oauth2/google/login`,
      payload
    );

    return response.data;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Google OAuth initiation failed:', appError);
    throw appError;
  }
};

/**
 * Complete Google OAuth login flow after Google redirects back
 * This is function 2: GET /oauth2/google/login/callback?state=...&code=...
 */
export const completeGoogleOAuthLogin = async (
  state: string,
  code: string,
  additionalParams?: Record<string, string>
): Promise<GoogleOAuthCallbackResponse> => {
  try {
    // Build query parameters
    const params = new URLSearchParams();
    params.append('state', state);
    params.append('code', code);

    // Add any additional Google OAuth parameters
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (key !== 'state' && key !== 'code') {
          params.append(key, value);
        }
      });
    }

    const response = await apiClient.chatbotGet<GoogleOAuthCallbackResponse>(
      `/oauth2/google/login/callback?${params.toString()}`
    );

    return response.data;
  } catch (error) {
    const appError = normalizeError(error);
    console.error('Google OAuth callback failed:', appError);
    throw appError;
  }
};

/**
 * Narrower than the `'subtype' in error` check used elsewhere in this file: an arbitrary thrown
 * object that happens to carry a `subtype` is not an AppError, and rethrowing it raw would put a
 * shape the callers do not handle in front of the merchant.
 */
function isAppError(error: unknown): error is AppError {
  return (
    !!error &&
    typeof error === 'object' &&
    typeof (error as AppError).subtype === 'string' &&
    typeof (error as AppError).httpStatusCode === 'number'
  );
}

/** What apiClient hands back in place of a thrown error — see api.ts. */
interface ApiErrorEnvelope {
  errorCode: number;
  errorMessage?: string;
  subtype?: string;
}

/**
 * Bind a FirstPromoter referral code (fpr) to an existing user.
 * Backend endpoint: POST /api/v1/users/bind-referral/{userId}
 *
 * Returns the updated UserProfile on success.
 * Throws AppError on failure (e.g. user already bound, code invalid, etc.)
 *
 * The rejection is explicit because the interceptor never throws for us: a 400 comes back
 * from apiClient as a RESOLVED response with status 200 and the error envelope as its body
 * (see api.ts, 'return error data instead of throwing'). Without this check the envelope was
 * returned as a UserProfile, written into the auth store, and rendered as
 * 'Referral code bound successfully' — which is WOR-1385's reported symptom, and it survived
 * the backend starting to reject invalid codes. signupUser() has guarded this the same way
 * all along.
 */
export const bindReferralCode = async (userId: string, fpr: string): Promise<UserProfile> => {
  try {
    const response = await apiClient.chatbotPost<UserProfile | ApiErrorEnvelope>(
      `/users/bind-referral/${userId}`,
      { fpr }
    );
    const data = response.data;
    if (data && typeof data === 'object' && 'errorCode' in data) {
      throw normalizeError(data);
    }
    return data as UserProfile;
  } catch (error) {
    // Already an AppError (the throw above, or the interceptor's own): normalizing a second
    // time loses the subtype — normalizeError keys off `errorCode`, which an AppError does not
    // carry, so invalid_referral_code came back out as internal_processing_error.
    if (isAppError(error)) {
      console.error('bindReferralCode failed:', error);
      throw error;
    }
    const appError = normalizeError(error);
    console.error('bindReferralCode failed:', appError);
    throw appError;
  }
};

/**
 * "Continue with Shopify" handoff (Onboarding v3): trade the one-time code the
 * ShopifyApp OAuth redirect hands us for a sign-in decision. Pass `confirm: true`
 * to re-submit the rotated `handoff` a `confirm_required` result returned, once
 * the merchant has agreed to bind/switch — never guess that flag's spelling, this
 * is the only call site.
 *
 * Note: never log `handoff`, `auth` (tokens) or `email` from the result — the
 * caller is responsible for keeping those out of its own logs too.
 */
export const postShopifyHandoff = async (
  handoff: string,
  confirm?: boolean
): Promise<ShopifyHandoffResult> => {
  try {
    const response = await apiClient.chatbotPost<ShopifyHandoffResult | ApiErrorEnvelope>(
      '/auth/shopify',
      confirm ? { handoff, confirm: true } : { handoff }
    );
    const data = response.data;
    if (data && typeof data === 'object' && 'errorCode' in data) {
      throw normalizeError(data);
    }
    return data as ShopifyHandoffResult;
  } catch (error) {
    // Already an AppError (the throw above, or the interceptor's own) — see
    // bindReferralCode's comment for why this must not be normalized twice.
    if (isAppError(error)) {
      console.error('postShopifyHandoff failed:', { subtype: error.subtype });
      throw error;
    }
    const appError = normalizeError(error);
    console.error('postShopifyHandoff failed:', { subtype: appError.subtype });
    throw appError;
  }
};
